import { RefreshRounded } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { FC, ReactElement, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormSelect, FormTextField } from 'src/components/form';
import { BasicDatePicker } from 'src/components/form/DatePicker';
import { Row, Section } from 'src/components/layout';
import { StatusStyleObject } from 'src/components/RefreshableStatusWidget';
import {
  FormFields as AllFormFields,
  INSURANCE_COVERAGE_OPTIONS,
  InsurancePriorityOptions,
  PatientAddressFields,
  PatientIdentifyingFields,
  RELATIONSHIP_TO_INSURED_OPTIONS,
  SEX_OPTIONS,
  STATE_OPTIONS,
} from 'src/constants';
import { dataTestIds } from 'src/constants/data-test-ids';
import { useApiClients } from 'src/hooks/useAppClients';
import { usePatientStore } from 'src/state/patient.store';
import {
  chooseJson,
  CoverageCheckWithDetails,
  EligibilityCheckSimpleStatus,
  InsuranceEligibilityCheckStatus,
  InsurancePlanDTO,
  InsurancePlanType,
  InsurancePlanTypes,
  isPostalCodeValid,
  mapEligibilityCheckResultToSimpleStatus,
  PatientPaymentBenefit,
  REQUIRED_FIELD_ERROR_MESSAGE,
} from 'utils';
import { CopayWidget } from './CopayWidget';
import { EligibilityDetailsDialog } from './EligibilityDetailsDialog';
import ShowMoreButton from './ShowMoreButton';

type InsuranceContainerProps = {
  ordinal: number;
  patientId: string;
  initialEligibilityCheck?: CoverageCheckWithDetails;
  removeInProgress?: boolean;
  handleRemoveClick?: () => void;
};

export const STATUS_TO_STYLE_MAP: Record<EligibilityCheckSimpleStatus, StatusStyleObject> = {
  ELIGIBLE: {
    bgColor: '#C8E6C9',
    textColor: '#1B5E20',
  },
  'NOT ELIGIBLE': {
    bgColor: '#FECDD2',
    textColor: '#B71C1C',
  },
  UNKNOWN: {
    bgColor: '#FECDD2',
    textColor: '#B71C1C',
  },
};

function mapInitialStatus(
  initialCheckResult: CoverageCheckWithDetails | undefined
): SimpleStatusCheckWithDate | undefined {
  if (initialCheckResult) {
    const status = mapEligibilityCheckResultToSimpleStatus(initialCheckResult);
    return {
      status: status.status,
      dateISO: status.dateISO,
      copay: initialCheckResult.copay,
      errors: initialCheckResult.errors,
    };
  }
  return undefined;
}

function mapSimpleStatusToDetailedStatus(simpleStatus: EligibilityCheckSimpleStatus): InsuranceEligibilityCheckStatus {
  switch (simpleStatus) {
    case 'ELIGIBLE':
      return InsuranceEligibilityCheckStatus.eligibilityConfirmed;
    case 'NOT ELIGIBLE':
      return InsuranceEligibilityCheckStatus.eligibilityNotConfirmed;
    case 'UNKNOWN':
    default:
      return InsuranceEligibilityCheckStatus.eligibilityNotChecked;
  }
}

function getErrorDetailsFromCoverageResponse(
  eligibilityCheck?: CoverageCheckWithDetails
): Array<{ code: string; text: string }> | undefined {
  if (!eligibilityCheck) return undefined;

  // First, try to extract errors from the errors field if available
  if (eligibilityCheck.errors && eligibilityCheck.errors.length > 0) {
    return eligibilityCheck.errors.map((error) => {
      // Extract code and text from FHIR CodeableConcept
      const code = error.code?.coding?.[0]?.code || error.code?.text || 'UNKNOWN';
      const text = error.code?.text || 'No error details available';
      return { code, text };
    });
  }

  return undefined;
}

interface SimpleStatusCheckWithDate {
  status: EligibilityCheckSimpleStatus;
  dateISO: string;
  copay?: PatientPaymentBenefit[];
  errors?: Array<{ code: { coding?: Array<{ code?: string; display?: string }>; text?: string } }>;
}

export const InsuranceContainer: FC<InsuranceContainerProps> = ({
  ordinal,
  patientId,
  removeInProgress: _removeInProgress,
  initialEligibilityCheck,
  handleRemoveClick: _handleRemoveClick,
}) => {
  const { t } = useTranslation();
  const _theme = useTheme();
  const { insurancePlans } = usePatientStore();

  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const [showEligibilityDetails, setShowEligibilityDetails] = useState(false);

  const [eligibilityStatus, setEligibilityStatus] = useState<SimpleStatusCheckWithDate | undefined>(
    mapInitialStatus(initialEligibilityCheck)
  );

  const { control, setValue, watch } = useFormContext();

  const { FormFields, LocalAddressFields, LocalIdentifyingFields } = useMemo(() => {
    const insurance = AllFormFields.insurance[ordinal - 1];

    const LocalAddressFields = [
      insurance.streetAddress.key,
      insurance.addressLine2.key,
      insurance.city.key,
      insurance.state.key,
      insurance.zip.key,
    ];

    const LocalIdentifyingFields = [
      insurance.firstName.key,
      insurance.middleName.key,
      insurance.lastName.key,
      insurance.birthDate.key,
      insurance.birthSex.key,
    ];
    return { FormFields: insurance, LocalAddressFields, LocalIdentifyingFields };
  }, [ordinal]);

  const patientAddressData = watch(PatientAddressFields);
  const patientIdentifyingData = watch(PatientIdentifyingFields);
  const localAddressData = watch(LocalAddressFields);
  const localIdentifyingData = watch(LocalIdentifyingFields);
  const selfSelected = watch(FormFields.relationship.key) === 'Self';
  const insurancePriority = watch(FormFields.insurancePriority.key);
  const sameAsPatientAddress = watch(FormFields.policyHolderAddressAsPatient.key, false);

  useEffect(() => {
    if (sameAsPatientAddress || selfSelected) {
      for (let i = 0; i < localAddressData.length; i++) {
        if (patientAddressData[i] && localAddressData[i] !== patientAddressData[i]) {
          setValue(LocalAddressFields[i], patientAddressData[i]);
        }
      }
      if (selfSelected) {
        for (let i = 0; i < localIdentifyingData.length; i++) {
          if (patientIdentifyingData[i] && localIdentifyingData[i] !== patientIdentifyingData[i]) {
            setValue(LocalIdentifyingFields[i], patientIdentifyingData[i]);
          }
        }
      }
    }
  }, [
    LocalAddressFields,
    LocalIdentifyingFields,
    localAddressData,
    localIdentifyingData,
    patientAddressData,
    patientIdentifyingData,
    sameAsPatientAddress,
    selfSelected,
    setValue,
  ]);

  const toggleMoreInfo = (): void => {
    setShowMoreInfo((prev) => !prev);
  };

  const { oystehrZambda } = useApiClients();

  const recheckEligibility = useMutation({
    mutationFn: async () => {
      // todo: show an alert when form has unsaved changes?
      const coverageToCheck = insurancePriority?.toLowerCase();
      try {
        return oystehrZambda?.zambda
          .execute({
            id: 'get-eligibility',
            patientId,
            coverageToCheck: coverageToCheck,
          })
          .then((res) => {
            console.log('eligibility check result', res);
            const json = chooseJson(res);
            const fullResult = coverageToCheck === 'secondary' ? json.secondary : json.primary;
            const simpleStatus = mapEligibilityCheckResultToSimpleStatus(fullResult);

            // Return combined data including the error details
            return {
              ...simpleStatus,
              errors: fullResult?.errors,
            };
          });
      } catch (error: any) {
        throw new Error(error.message);
      }
    },
  });

  const handleRecheckEligibility = async (): Promise<void> => {
    try {
      const result = await recheckEligibility.mutateAsync();
      if (result) {
        setEligibilityStatus(result);
      } else {
        console.error('Error rechecking eligibility:', 'No result returned');
      }
      console.log('Eligibility check result:', result);
    } catch (error) {
      console.error('Error rechecking eligibility:', error);
    }
  };

  const TitleWidget = (): ReactElement => {
    const chipColors = STATUS_TO_STYLE_MAP[eligibilityStatus?.status ?? 'UNKNOWN'];
    const lastRefreshDateString = (() => {
      const dateISO = eligibilityStatus?.dateISO ?? '';
      if (dateISO) {
        try {
          const dt = new Date(dateISO);
          return t('patientProfile.text.lastChecked', { date: dt.toLocaleDateString() });
        } catch {
          return '';
        }
      }
      return '';
    })();

    const statusLabel =
      eligibilityStatus?.status === 'ELIGIBLE'
        ? t('patientProfile.options.eligibility.eligible')
        : eligibilityStatus?.status === 'NOT ELIGIBLE'
          ? t('patientProfile.options.eligibility.not_eligible')
          : t('patientProfile.options.eligibility.unknown');

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
        {/* Status badge and View Details button in same row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Chip
            label={statusLabel}
            sx={{
              backgroundColor: chipColors.bgColor,
              color: chipColors.textColor,
              borderRadius: '8px',
              padding: '0 9px',
              height: '24px',
              '& .MuiChip-label': {
                padding: 0,
                fontWeight: 'bold',
                fontSize: '0.7rem',
              },
            }}
          />
          {(initialEligibilityCheck || eligibilityStatus) && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowEligibilityDetails(true)}
              sx={{
                textTransform: 'none',
                fontSize: '12px',
                borderRadius: '16px',
                minWidth: 'auto',
                px: 2,
                py: 0.5,
              }}
            >
              {t('patientProfile.buttons.viewDetails')}
            </Button>
          )}
        </Box>
        {/* Last checked date and refresh button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            sx={{
              fontFamily: 'Rubik',
              color: 'rgba(0, 0, 0, 0.6)',
              fontSize: '12px',
              lineHeight: '15px',
              fontWeight: '400',
            }}
          >
            {lastRefreshDateString}
          </Typography>
          <IconButton onClick={handleRecheckEligibility} size="small">
            {recheckEligibility.isPending ? <CircularProgress size="24px" /> : <RefreshRounded color="primary" />}
          </IconButton>
        </Box>
      </Box>
    );
  };

  const copayBenefits = eligibilityStatus?.copay ?? [];

  // Get the most current eligibility data, combining initial data with any updates
  const getCurrentEligibilityData = (): CoverageCheckWithDetails | undefined => {
    if (!initialEligibilityCheck && !eligibilityStatus) {
      return undefined;
    }

    // If we have updated eligibility status from a recheck, merge it with initial data
    if (eligibilityStatus && initialEligibilityCheck) {
      const mergedData = {
        ...initialEligibilityCheck,
        status: mapSimpleStatusToDetailedStatus(eligibilityStatus.status),
        dateISO: eligibilityStatus.dateISO,
        // Prefer eligibilityStatus.copay if it has items, otherwise use initialEligibilityCheck.copay
        copay:
          eligibilityStatus.copay && eligibilityStatus.copay.length > 0
            ? eligibilityStatus.copay
            : initialEligibilityCheck.copay,
        errors: eligibilityStatus.errors || initialEligibilityCheck.errors,
      };

      return mergedData;
    }

    // If we only have initial data, use that
    if (initialEligibilityCheck) {
      return initialEligibilityCheck;
    }

    // If we only have eligibility status (shouldn't happen in practice), create minimal data
    if (eligibilityStatus) {
      return {
        status: mapSimpleStatusToDetailedStatus(eligibilityStatus.status),
        dateISO: eligibilityStatus.dateISO,
        copay: eligibilityStatus.copay,
        errors: eligibilityStatus.errors,
        subscriberId: '',
        payorRef: '',
      };
    }

    return undefined;
  };

  return (
    <Section
      title={t('patientProfile.sections.insuranceInformation')}
      dataTestId="insuranceContainer"
      titleWidget={<TitleWidget />}
    >
      <Box
        sx={{
          marginLeft: '12px',
          marginTop: 2,
        }}
      >
        <CopayWidget copay={copayBenefits} />
      </Box>
      <Row label={t('patientProfile.fields.insuranceType')} required dataTestId={dataTestIds.insuranceContainer.type}>
        <FormSelect
          name={FormFields.insurancePriority.key}
          control={control}
          readOnly={true}
          defaultValue={ordinal === 1 ? 'Primary' : 'Secondary'}
          options={INSURANCE_COVERAGE_OPTIONS.map((option) => ({
            ...option,
            label:
              option.value === 'Primary'
                ? t('patientProfile.options.insurancePriority.primary')
                : t('patientProfile.options.insurancePriority.secondary'),
          }))}
          rules={{
            required: REQUIRED_FIELD_ERROR_MESSAGE,
            validate: (value, context) => {
              // todo: this validation concept would be good to lift into the paperwork validation engine
              const otherGroupKey = InsurancePriorityOptions.find((key) => key !== FormFields.insurancePriority.key);
              let otherGroupValue: 'Primary' | 'Secondary' | undefined;
              if (otherGroupKey) {
                otherGroupValue = context[otherGroupKey];
              }
              if (otherGroupValue === value) {
                return t('patientProfile.errors.duplicatePlan', { value: value.toLowerCase() });
              }
              return true;
            },
          }}
        />
      </Row>
      <Row
        label={t('patientProfile.fields.insuranceCarrier')}
        required
        dataTestId={dataTestIds.insuranceContainer.insuranceCarrier}
      >
        <Controller
          name={FormFields.insuranceCarrier.key}
          control={control}
          rules={{
            required: REQUIRED_FIELD_ERROR_MESSAGE,
            validate: (value) => insurancePlans.some((option) => `Organization/${option.id}` === value?.reference),
          }}
          render={({ field: { value }, fieldState: { error } }) => {
            const isLoading = insurancePlans.length === 0;

            const selectedOption = insurancePlans.find((option) => `Organization/${option.id}` === value?.reference);
            return (
              <Autocomplete
                options={insurancePlans}
                loading={isLoading}
                readOnly={true}
                loadingText={t('patientProfile.text.loading')}
                value={selectedOption ?? ({} as InsurancePlanDTO)}
                isOptionEqualToValue={(option, value) => {
                  return option?.id === value?.id;
                }}
                getOptionLabel={(option) =>
                  option.payerId || option.name ? `${option?.payerId || 'N/A'} - ${option?.name || 'N/A'}` : ''
                }
                onChange={(_, newValue) => {
                  if (newValue) {
                    setValue(
                      FormFields.insuranceCarrier.key,
                      { reference: `Organization/${newValue.id}`, display: newValue.name },
                      { shouldDirty: true }
                    );
                  } else {
                    setValue(FormFields.insuranceCarrier.key, null);
                  }
                }}
                disableClearable
                fullWidth
                renderInput={(params) => (
                  <TextField {...params} variant="standard" error={!!error} required helperText={error?.message} />
                )}
              />
            );
          }}
        />
      </Row>
      <Row
        label={t('patientProfile.fields.insurancePlanType')}
        dataTestId={dataTestIds.insuranceContainer.insurancePlanType}
      >
        <Controller
          name={FormFields.insurancePlanType.key}
          control={control}
          rules={{
            validate: (value) => !value || InsurancePlanTypes.some((option) => option.candidCode === value),
          }}
          render={({ field: { value }, fieldState: { error } }) => {
            const selectedOption = InsurancePlanTypes.find((option) => option.candidCode === `${value}`);
            return (
              <Autocomplete
                options={InsurancePlanTypes}
                readOnly={true}
                value={selectedOption ?? ({} as InsurancePlanType)}
                isOptionEqualToValue={(option, value) => option?.candidCode === value?.candidCode}
                getOptionLabel={(option) =>
                  option.candidCode || option.label ? `${option.candidCode} - ${option.label}` : ''
                }
                onChange={(_, newValue) => {
                  if (newValue) {
                    setValue(FormFields.insurancePlanType.key, newValue.candidCode, { shouldDirty: true });
                  } else {
                    setValue(FormFields.insurancePlanType.key, null, { shouldDirty: true });
                  }
                }}
                fullWidth
                renderInput={(params) => (
                  <TextField {...params} variant="standard" error={!!error} helperText={error?.message} />
                )}
              />
            );
          }}
        />
      </Row>
      <Row
        label={t('patientProfile.fields.memberId')}
        required
        inputId={FormFields.memberId.key}
        dataTestId={dataTestIds.insuranceContainer.memberId}
      >
        <FormTextField
          id={FormFields.memberId.key}
          name={FormFields.memberId.key}
          control={control}
          InputProps={{ readOnly: true }}
          rules={{ required: REQUIRED_FIELD_ERROR_MESSAGE }}
        />
      </Row>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <ShowMoreButton
          onClick={toggleMoreInfo}
          isOpen={showMoreInfo}
          dataTestId={dataTestIds.insuranceContainer.showMoreButton}
        />
      </Box>
      {showMoreInfo && (
        <>
          <Row
            label={t('patientProfile.fields.policyHoldersFirstName')}
            required
            inputId={FormFields.firstName.key}
            dataTestId={dataTestIds.insuranceContainer.policyHoldersFirstName}
          >
            <FormTextField
              id={FormFields.firstName.key}
              name={FormFields.firstName.key}
              disabled={selfSelected}
              InputProps={{ readOnly: true }}
              control={control}
              rules={{ required: REQUIRED_FIELD_ERROR_MESSAGE }}
            />
          </Row>
          <Row
            label={t('patientProfile.fields.policyHoldersMiddleName')}
            inputId={FormFields.middleName.key}
            dataTestId={dataTestIds.insuranceContainer.policyHoldersMiddleName}
          >
            <FormTextField
              id={FormFields.middleName.key}
              name={FormFields.middleName.key}
              control={control}
              InputProps={{ readOnly: true }}
              disabled={selfSelected}
            />
          </Row>
          <Row
            label={t('patientProfile.fields.policyHoldersLastName')}
            required
            inputId={FormFields.lastName.key}
            dataTestId={dataTestIds.insuranceContainer.policyHoldersLastName}
          >
            <FormTextField
              id={FormFields.lastName.key}
              name={FormFields.lastName.key}
              disabled={selfSelected}
              InputProps={{ readOnly: true }}
              control={control}
              rules={{ required: REQUIRED_FIELD_ERROR_MESSAGE }}
            />
          </Row>
          <Row
            label={t('patientProfile.fields.policyHoldersDateOfBirth')}
            required
            dataTestId={dataTestIds.insuranceContainer.policyHoldersDateOfBirth}
          >
            <BasicDatePicker
              name={FormFields.birthDate.key}
              control={control}
              rules={{ required: REQUIRED_FIELD_ERROR_MESSAGE }}
              disabled={true}
            />
          </Row>
          <Row
            label={t('patientProfile.fields.policyHoldersSex')}
            required
            dataTestId={dataTestIds.insuranceContainer.policyHoldersSex}
          >
            <FormSelect
              name={FormFields.birthSex.key}
              control={control}
              readOnly={true}
              options={SEX_OPTIONS.map((option) => ({
                ...option,
                label: t(`patientProfile.options.sex.${option.value.toLowerCase()}`),
              }))}
              disabled={selfSelected}
              rules={{ required: REQUIRED_FIELD_ERROR_MESSAGE }}
            />
          </Row>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '5px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '0px' }}>
              <Controller
                key={sameAsPatientAddress}
                name={FormFields.policyHolderAddressAsPatient.key}
                control={control}
                render={({ field: { value, ...field } }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        data-testid={dataTestIds.insuranceContainer.policyHolderAddressCheckbox}
                        checked={value}
                        onChange={(e) => {
                          const checked = (e.target as HTMLInputElement).checked;
                          setValue(FormFields.policyHolderAddressAsPatient.key, checked, { shouldDirty: true });
                        }}
                        disabled={selfSelected}
                      />
                    }
                    label={<Typography>{t('patientProfile.fields.policyHolderSameAsPatient')}</Typography>}
                  />
                )}
              />
            </Box>
          </Box>
          <Row
            label={t('patientProfile.fields.streetAddress')}
            inputId={FormFields.streetAddress.key}
            required
            dataTestId={dataTestIds.insuranceContainer.streetAddress}
          >
            <FormTextField
              id={FormFields.streetAddress.key}
              name={FormFields.streetAddress.key}
              disabled={(sameAsPatientAddress || selfSelected) && Boolean(patientAddressData[0])}
              InputProps={{ readOnly: true }}
              control={control}
              rules={{ required: REQUIRED_FIELD_ERROR_MESSAGE }}
            />
          </Row>
          <Row
            label={t('patientProfile.fields.addressLine2')}
            inputId={FormFields.addressLine2.key}
            dataTestId={dataTestIds.insuranceContainer.addressLine2}
          >
            <FormTextField
              id={FormFields.addressLine2.key}
              name={FormFields.addressLine2.key}
              disabled={sameAsPatientAddress || selfSelected}
              InputProps={{ readOnly: true }}
              control={control}
            />
          </Row>
          <Row label={t('patientProfile.fields.cityStateZip')} required>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormTextField
                data-testid={dataTestIds.insuranceContainer.city}
                name={FormFields.city.key}
                disabled={(sameAsPatientAddress || selfSelected) && Boolean(patientAddressData[2])}
                InputProps={{ readOnly: true }}
                control={control}
                rules={{ required: REQUIRED_FIELD_ERROR_MESSAGE }}
              />
              <Controller
                name={FormFields.state.key}
                control={control}
                rules={{
                  required: REQUIRED_FIELD_ERROR_MESSAGE,
                }}
                render={({ field: { value }, fieldState: { error } }) => {
                  return (
                    <Autocomplete
                      options={STATE_OPTIONS.map((option) => option.value)}
                      disabled={(sameAsPatientAddress || selfSelected) && Boolean(patientAddressData[3])}
                      readOnly={true}
                      value={value ?? ''}
                      onChange={(_, newValue) => {
                        if (newValue) {
                          setValue(FormFields.state.key, newValue);
                        } else {
                          setValue(FormFields.state.key, '');
                        }
                      }}
                      disableClearable
                      fullWidth
                      renderInput={(params) => (
                        <TextField
                          data-testid={dataTestIds.insuranceContainer.state}
                          {...params}
                          variant="standard"
                          error={!!error}
                          required
                          helperText={error?.message}
                        />
                      )}
                    />
                  );
                }}
              />
              <FormTextField
                data-testid={dataTestIds.insuranceContainer.zip}
                name={FormFields.zip.key}
                control={control}
                disabled={(sameAsPatientAddress || selfSelected) && Boolean(patientAddressData[4])}
                InputProps={{ readOnly: true }}
                rules={{
                  validate: (value: string) => isPostalCodeValid(value) || t('patientProfile.errors.zipDigits'),
                  required: REQUIRED_FIELD_ERROR_MESSAGE,
                }}
              />
            </Box>
          </Row>
          <Row
            label={t('patientProfile.fields.relationshipToInsured')}
            required
            dataTestId={dataTestIds.insuranceContainer.relationship}
          >
            <FormSelect
              name={FormFields.relationship.key}
              control={control}
              readOnly={true}
              options={RELATIONSHIP_TO_INSURED_OPTIONS.map((option) => ({
                ...option,
                label: t(
                  `patientProfile.options.relationship.${option.value
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '_')
                    .replace(/^_+|_+$/g, '')}`
                ),
              }))}
              rules={{ required: REQUIRED_FIELD_ERROR_MESSAGE }}
            />
          </Row>
          <Row
            label={t('patientProfile.fields.additionalInformation')}
            inputId={FormFields.additionalInformation.key}
            dataTestId={dataTestIds.insuranceContainer.additionalInformation}
          >
            <FormTextField
              id={FormFields.additionalInformation.key}
              name={FormFields.additionalInformation.key}
              InputProps={{ readOnly: true }}
              control={control}
            />
          </Row>
          {/* <LoadingButton
            data-testid={dataTestIds.insuranceContainer.removeButton}
            onClick={_handleRemoveClick}
            variant="text"
            loading={_removeInProgress}
            sx={{
              color: _theme.palette.error.main,
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 500,
              display: _handleRemoveClick !== undefined ? 'flex' : 'none',
              alignItems: 'center',
              justifyContent: 'flex-start',
              padding: '0',
              width: 'fit-content',
            }}
          >
            {t('patientProfile.buttons.removeInsurance')}
          </LoadingButton> */}
        </>
      )}

      <EligibilityDetailsDialog
        open={showEligibilityDetails}
        onClose={() => setShowEligibilityDetails(false)}
        eligibilityCheck={getCurrentEligibilityData()}
        simpleStatus={eligibilityStatus?.status}
        errorDetails={getErrorDetailsFromCoverageResponse(getCurrentEligibilityData())}
      />
    </Section>
  );
};
